<template>
  <div :class="['status-container', isCreate ? 'create' : 'edit']">
    <div v-if="!hasAutomationFeature" class="upgrade_message">
      <h4 class="upgrade_message--heading">Upgrade Required</h4>
      <p class="upgrade_message--message">Please <a @click="openBillingPortal">upgrade</a> your subscription in order to get more triggers and actions for status automations.</p>
    </div>

    <grid-row>
      <grid-column>
        <form-field-select
            label="Trigger"
            :disabled="!isCreate"
            v-model="formData.hook"
            :validation-errors="validationErrors.hook"
        >
          <option
            :disabled="hookOptionIsDisabled(option)"
            v-for="option in ENUMS.AutomationHookEnum"
            :value="option.value"
          >{{ option.label }}{{ hookOptionIsDisabled(option) ? ' (' + hookOptionIsDisabled(option) + ')' : '' }}</option>
        </form-field-select>
      </grid-column>

      <grid-column v-if="[3,5].includes(formData.hook)">
        <form-field-time-in-minutes
            class="is-4"
            label="Time Span"
            v-model="formData.minutes"
            :validation-errors="validationErrors.minutes"
        ></form-field-time-in-minutes>
      </grid-column>

      <grid-column>
        <form-field-select
            label="Action"
            v-model="action"
            :disabled="!isCreate"
        >
          <option
            :disabled="actionOptionIsDisabled(option)"
            v-for="option in $store.state.enums.automationTypeEnum"
            :value="option.value"
          >{{ option.label }}{{ actionOptionIsDisabled(option) ? ' (' + actionOptionIsDisabled(option) + ')' : '' }}</option>
        </form-field-select>
      </grid-column>

    </grid-row>

    <!-- Add Task Automation-->
    <template v-if="action === MODELS.AddTaskAutomation.class_name">
      <!--Create-->
      <p v-if="isCreate">Save the automation before adding tasks to this automation</p>

      <!--Update-->
      <template v-else>
        <div
            v-for="(template, index) in formData.taskTemplates"
            :key="template.id"
            class="record-hover-item columns is-mobile is-size-7"
        >
          <!--This column below is here as a bandaid because .record-hover-item has a padding: 0 with important-->
          <div class="column is-narrow" style="width: 20px"></div>
          <div class="column">
            {{ template.label || 'New Task' }}
            <span class="is-size-8 has-text-danger">{{ template.is_required_to_change_status ? ' (Required) ' : '' }}</span>
            <span v-if="template.assign_to_id" class="is-size-8">- {{ getAssignedUser(template) }}</span>
          </div>

          <div class="column is-narrow" style="min-width: 20px">
            <b-dropdown aria-role="menu" position="is-bottom-left">
              <template #trigger="{active}">
                <icon-default
                  class="edit-icon"
                  size="is-small"
                  color="is-primary"
                  icon="edit"
                />
              </template>

              <b-dropdown-item :focusable="false" style="width: 400px" custom>
                <task-template-form
                  :form-data="template"
                  :add-task-automation-id="automation.id"
                  :show-buttons="true"
                  @updated="updateTaskTemplate"
                  @deleted="removeTaskTemplate"
                ></task-template-form>
              </b-dropdown-item>
            </b-dropdown>
          </div>

          <!--This column below is here as a bandaid because .record-hover-item has a padding: 0 with important-->
          <div class="column is-narrow" style="width: 20px"></div>
        </div>

        <b-dropdown ref="addTaskAutomationTrigger" aria-role="menu" position="is-bottom-right">
          <template #trigger="{active}">
            <b-button class="ml-0" type="is-primary" size="is-small" outlined>Add Task</b-button>
          </template>

          <b-dropdown-item :focusable="false" style="width: 600px" custom>
            <b-tabs class="pt-50" v-model="associateTaskTemplateActiveTab">
              <b-tab-item label="Create New Template" value="NewTask">
                <task-template-form
                  class="status-add-checklist-item"
                  :show-buttons="true"
                  :add-task-automation-id="automation.id"
                  @created="addTaskTemplate"
                ></task-template-form>
              </b-tab-item>

              <b-tab-item label="Existing Templates" value="ExistingTemplate">
                <div class="task-templates">
                  <template v-if="existingTemplates.length !== 0">
                    <button-link
                      class="task-templates--item"
                      size="is-normal"
                      v-for="template in existingTemplates"
                      :key="template.id"
                      @click="attachTemplate(template)"
                    >
                      {{ template.label }} - {{ getAssignedUser(template) }}
                    </button-link>
                  </template>
                  <template v-else>
                    <p class="task-templates--empty-message">You don't have any task templates</p>
                  </template>
                </div>
              </b-tab-item>

              <b-tab-item label="Existing Lists" value="ExistingList">
                <div class="task-templates">
                  <template v-if="existingTaskLists.length !== 0">
                    <button-link
                      class="task-templates--item"
                      size="is-normal"
                      v-for="list in existingTaskLists"
                      :key="list.id"
                      @click="attachTaskList(list)"
                    >{{ list.label }} - {{ list.task_templates.length }} task templates</button-link>
                  </template>
                  <template v-else>
                    <p class="task-templates--empty-message">You don't have any task lists</p>
                  </template>
                </div>
              </b-tab-item>
            </b-tabs>
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </template>

    <!-- Notify Users Automation -->
    <status-notify-users-automation
        v-if="action === MODELS.NotifyUsersAutomation.class_name"
        :users="formData.usersToNotify"
        @setUsers="setUsers"
    ></status-notify-users-automation>

    <!-- Manage Users Automation -->
    <status-manage-users-automation
        v-if="action === MODELS.ManageUsersAutomation.class_name"
        :add-users="formData.usersToAddToRecord"
        :remove-users="formData.usersToRemoveFromRecord"
        @setUsers="setUsers"
    ></status-manage-users-automation>

    <!-- Webhook Automation -->
    <form-field-input
      v-if="action === MODELS.SendWebhookAutomation.class_name"
      label="What URL should we send the lead data to?"
      v-model="formData.webhook_url"
      :validation-errors="validationErrors.webhook_url"
    ></form-field-input>

    <!-- Temperature Automation -->
    <form-field-select
      v-if="action === MODELS.UpdateRecordTemperatureAutomation.class_name"
      label="What temperature should the record be?"
      v-model="formData.temperature"
      :validation-errors="validationErrors.temperature"
    >
      <option v-for="option in $store.state.enums.temperatureEnum" :value="option.value">{{ option.label }}</option>
    </form-field-select>

    <!-- Email Automation -->
    <template v-if="action === MODELS.EmailAutomation.class_name">
      <grid-row>
        <grid-column class="pb-0">
          <form-field-select-user-emails
            v-model="formData.user_id"
            :validation-errors="validationErrors.user_id"
          ></form-field-select-user-emails>
        </grid-column>
        <grid-column class="pb-0">
          <form-field-input
            label="From Name"
            v-model="formData.from_name"
            :validation-errors="validationErrors.from_name"
          ></form-field-input>
        </grid-column>
      </grid-row>

      <form-field-editor
        label="Subject"
        v-model="formData.subject"
        type="shortCodesOnly"
        short-code-type="record"
        is-single-line
        :validation-errors="validationErrors.subject"
      ></form-field-editor>

      <form-field-editor
        v-model="formData.body"
        type="email"
        short-code-type="record"
        :validation-errors="validationErrors.body"
      ></form-field-editor>
    </template>

    <!-- Text Automation -->
    <template v-if="action === MODELS.TextAutomation.class_name">
      <grid-row>
        <grid-column :size="6">
          <form-field-select-integration-phone-number
            label="From"
            v-model="formData.integration_phone_number_id"
            :has-empty-option="false"
          >
          </form-field-select-integration-phone-number>
        </grid-column>
      </grid-row>

      <div class="app-label">
        <label>Message </label>
      </div>
      <form-field-editor
        v-model="formData.message"
        type="textContactWithRecord"
        short-code-type="record"
        :validation-errors="validationErrors.message"
      ></form-field-editor>
    </template>

    <!-- Button -->
    <div class="is-full mt-100 is-clearfix">
      <b-button
          v-if="!isCreate"
          type="is-text"
          size="is-small"
          @click="remove"
          class="archive-btn is-pulled-left"
          :loading="isDeleting"
          :disabled="isSaving || isDeleting"
      >Delete Item</b-button>
      <b-button
          outlined
          type="is-primary"
          size="is-small"
          @click="createAutomation"
          :loading="isSaving"
          :disabled="isSaving || isDeleting"
          class="is-pulled-right"
      >{{ isCreate ? 'Create Automation' : 'Update Automation' }}</b-button>
    </div>
  </div>
</template>

<script>
import StatusNotifyUsersAutomation from './StatusNotifyUsersAutomation';
import StatusManageUsersAutomation from './StatusManageUsersAutomation';
import TaskTemplateForm from "./TaskTemplateForm";
import {ENUMS} from "../../../../services/enums";
import {MODELS} from "../../../../services/models";
import api from "../../../../api";

export default {
  name: 'StatusAddAutomation',
  components: {
    TaskTemplateForm,
    StatusNotifyUsersAutomation,
    StatusManageUsersAutomation,
  },
  props: {
    status: Object,
    automation: Object
  },
  data() {
    return {
      isSaving: false,
      isDeleting: false,
      isAddTaskOpen: false,
      validationErrors: {},
      formData: {},
      action: MODELS.AddTaskAutomation.class_name,
      associateTaskTemplateActiveTab: 'NewTask',
      existingTemplates: [],
      existingTaskLists: [],
      loadingExistingTemplates: false,
      loadingExistingTaskLists: false,
    }
  },
  watch: {
    action(newValue) {
      if (newValue === MODELS.AddTaskAutomation.class_name) {
        if (this.formData.taskTemplates.length === 0) {
          this.isAddTaskOpen = true
        }
      }
    }
  },
  computed: {
    users() {
      return this.$store.getters.activeUsers
    },
    hasAutomationFeature() {
      return this.$store.getters.account.has_automation_feature
    },
    isCreate() {
      return (!this.automation);
    },
  },
  created() {
    this.initForm();
  },
  mounted() {
    if (this.automation && this.automation.class_name === MODELS.AddTaskAutomation.class_name) {
      Promise.all([
        this.loadExistingTemplates(),
        this.loadExistingTaskLists()
      ]).then(() => {})
    }
  },
  methods: {
    getAssignedUser(taskTemplate) {
      const user = this.users.find(u => u.id === taskTemplate.assign_to_id)
      return (user) ? user.full_name : 'No User Assigned'
    },
    attachTemplate(template) {
      return api.taskTemplates.attachToAutomation(template.id, this.automation.id)
      .then((res) => {
        this.addTaskTemplate(template)
        this.$buefy.toast.open({
          message: "Task template added on the automation",
          type: 'is-success'
        })
      })
    },
    attachTaskList(list) {
      api.taskLists.attachToAutomation(list.id, this.automation.id)
        .then((res) => {
          res.data.data.forEach((template) => this.addTaskTemplate(template))
          this.$buefy.toast.open({
            message: "Task templates was added on the automation",
            type: 'is-success'
          })
        })
    },
    loadExistingTemplates() {
      this.loadingExistingTemplates = true
      return api.taskTemplates.all().then((response) => {
        this.existingTemplates = response.data
      }).finally(() => {
        this.loadingExistingTemplates = false
      })
    },
    loadExistingTaskLists() {
      this.loadingExistingTaskLists = true
      return api.taskLists.all().then((response) => {
        this.existingTaskLists = response.data
      }).finally(() => {
        this.loadingExistingTaskLists = false
      })
    },
    hookOptionIsDisabled(option) {
      // Enter Status Hook is A Core Feature
      if (option.value === ENUMS.AutomationHookEnum.EnterStatus.value) {
        return false;
      }

      // Disable Everything else if they do not have the Automation Feature
      if (!this.hasAutomationFeature) {
        return "need to upgrade";
      }

      if (option.value === ENUMS.AutomationHookEnum.XTimeBeforeStatusDue.value && this.status.state !== ENUMS.StatusStateEnum.Active.value) {
        return "Only for active statuses";
      }

      // Everything else can be enabled
      return false;
    },
    actionOptionIsDisabled(option) {
      // Add Task Automation is A Core Feature
      if (option.value === ENUMS.AutomationTypeEnum.AddTask.value) {
        return false;
      }

      // Disable Everything else if they do not have the Automation Feature
      if (!this.hasAutomationFeature) {
        return "need to upgrade";
      }

      // Disable Email Primary Contact if they dont have an email integration
      if (option.value === ENUMS.AutomationTypeEnum.EmailPrimaryContact.value && !this.$store.getters.emailIsIntegrated) {
        return "no email integration";
      }

      // Disable Text Primary Contact if they dont have a phone integration
      if (option.value === ENUMS.AutomationTypeEnum.TextPrimaryContact.value && !this.$store.getters.hasPhoneIntegration) {
        return "no phone integration";
      }

      if (option.value === ENUMS.AutomationHookEnum.XTimeBeforeStatusDue.value && this.status.state !== ENUMS.StatusStateEnum.Active.value) {
        return "only available for active statuses"
      }

      // Everything else can be enabled
      return false;
    },
    updateTaskTemplate(template) {
      this.$refs.addTaskAutomationTrigger.$el.click();
      this.formData.taskTemplates = this.formData.taskTemplates.map(item => {
        return (item.id === template.id) ? {...item, ...template} : item
      })
    },
    addTaskTemplate(taskTemplate) {
      this.$refs.addTaskAutomationTrigger.$el.click();
      this.formData.taskTemplates.push(taskTemplate);
    },
    removeTaskTemplate(template) {
      const templateIndex = this.formData.taskTemplates.findIndex(t => t.id === template.id)
      this.formData.taskTemplates.splice(templateIndex, 1)

      this.$buefy.toast.open({
        message: "Task template was removed on the automation",
        type: 'is-success'
      })
    },
    initForm() {
      if (this.isCreate) {
        this.action = MODELS.AddTaskAutomation.class_name;
        this.isAddTaskOpen = true;

        const integrationPhoneNumbers = this.$store.state.market.integrationPhoneNumbers;
        const integrationPhoneNumberId = integrationPhoneNumbers.length ? integrationPhoneNumbers[0].id : null;

        this.formData = {
          user_id: this.$store.getters.authUser.id,
          status_id: this.status.id,
          hook: 1,
          minutes: 60,
          taskTemplates: [],
          usersToAddToRecord: [],
          usersToRemoveFromRecord: [],
          usersToNotify: [],
          webhook_url: '',
          temperature: ENUMS.TemperatureEnum.Warm.value,
          isOpen: false,
          clientId: this.$utils.generateClientId(),
          integration_phone_number_id: integrationPhoneNumberId,
          message: null
        }
      } else {
        this.formData = this.$utils.clone(this.automation);

        this.action = this.formData.class_name;
      }
    },
    setUsers(data) {
      this.formData[data.prop] = data.val;
    },
    createAutomation() {
      const api = this.action.substring(11, this.action.length);
      const method = this.isCreate ? 'create' : 'update';

      if (![3,5].includes(this.formData.hook)) {
        this.formData.minutes = null;
      }

      this.isSaving = true;

      this.$api.automations[api][method](this.formData).then(res => {
        const message = this.isCreate ? 'Created Automation' : 'Updated Automation';

        this.$buefy.toast.open({message, type: 'is-success'});
        this.validationErrors = {};

        if (this.isCreate) {
          this.initForm();

          res.data.isOpen = false;
          res.data.clientId = this.$utils.generateClientId();
          this.$emit('created', res.data);
        } else {
          this.$emit('updated', this.formData);
        }
      }, error => {
        if (error.response.status === 422) {
          for (let [key, value] of Object.entries(error.response.data.errors)) {
            if (key.slice(0, 14) === 'taskTemplates') {
              const itemIndex = key.slice(15, 16);
              const itemProp = key.slice(17, key.length);
              const itemsCopy = this.$utils.clone(this.formData.taskTemplates)

              itemsCopy[itemIndex].validationErrors[itemProp] = value;
              itemsCopy[itemIndex].isOpen = true;

              this.formData.taskTemplates = itemsCopy;
            }
          }

          this.validationErrors = error.response.data.errors;
        }
      }).finally(() => {
        this.isSaving = false;
      });
    },
    remove() {
      const api = this.action.substring(11, this.action.length);

      this.$utils.showDeleteDialog('this automation', () => {
        this.isDeleting = true;
        this.$api.automations[api].delete(this.formData.id).then(res => {
          this.$emit('deleted', this.formData);
          this.$buefy.toast.open({message: 'Deleted Automation', type: 'is-success'});
        }).finally(() => {
          this.isDeleting = false;
        });
      });
    },
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../styles/variables/variables";

.status-container {
  background: $white;

  &.create {
    border: 1px solid $border;
    padding: 10px 20px 10px;
    margin-bottom: 20px;
  }

  &.edit {
    border: 1px solid $border;
    padding: 10px 20px 10px;
    margin-bottom: 20px;
  }
}

.upgrade_message {
  padding: 10px 0 2rem;
  font-style: italic;

  &--heading {
    margin-bottom: 6px;
    font-size: 1.125rem;
  }

  &--message {
    color: $text-light;
    line-height: 1.4;
  }
}

.task-templates {
  padding: 15px 0;

  &--item {
    margin-bottom: 12px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &--empty-message {
    font-size: 1rem;
    color: $text-light;
    line-height: 1.4;
  }
}
</style>
